import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DashboardService } from '../../Services/dashboard.services';

/** Compact row rendered by the list view of the activities box. */
interface MyActivityRow {
  id: number;
  title: string;
  deadline: Date | null;
  /** Deadline is in the past, shown as a red badge. */
  overdue: boolean;
}

/**
 * List view of the activities dashboard box: the open activities the logged in user created or is
 * responsible for execution or follow-up of. The endpoint only returns open activities, so the
 * rows carry no status - a past deadline is the only thing worth flagging.
 */
@Component({
  selector: 'app-my-activities-widget',
  templateUrl: './my-activities-widget.component.html',
  styleUrls: ['./my-activities-widget.component.scss'],
  standalone: false
})
export class MyActivitiesWidgetComponent implements OnInit, OnDestroy {
  rows: MyActivityRow[] = [];
  loading = true;

  private subscriptions = new Subscription();

  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadRows();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openActivity(row: MyActivityRow): void {
    this.router.navigate(['../follow-up/activities/edit', row.id], {
      relativeTo: this.route
    });
  }

  private loadRows(): void {
    this.loading = true;

    this.subscriptions.add(
      this.dashboardService
        .getMyActivities()
        .pipe(catchError(() => of([] as any[])))
        .subscribe((activities: any[]) => {
          this.rows = (activities || [])
            .map(activity => this.toRow(activity))
            .sort(
              (left, right) => this.deadlineValue(left) - this.deadlineValue(right)
            );

          this.loading = false;
        })
    );
  }

  private toRow(activity: any): MyActivityRow {
    const deadline = activity.EndDate ? new Date(activity.EndDate) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return {
      id: activity.Id,
      title: activity.Title,
      deadline,
      overdue: !!deadline && deadline < today
    };
  }

  /** Rows without a deadline sort last. */
  private deadlineValue(row: MyActivityRow): number {
    return row.deadline ? row.deadline.getTime() : Number.MAX_SAFE_INTEGER;
  }
}
